import React, { useState } from "react";
import { Truck, Plus, Pencil, Trash2, Search, Phone, Mail, MapPin } from "lucide-react";
import { useApp } from "../context/AppContext";
import { maskCPF } from "../utils/masks";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const vazio = { nome: "", documento: "", telefone: "", email: "", endereco: "", categoria: "", observacoes: "" };

const Fornecedores = () => {
  const { fornecedores, salvarFornecedor, excluirFornecedor } = useApp();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(vazio);
  const [q, setQ] = useState("");

  const abrirNovo = () => { setEditing(null); setForm(vazio); setOpen(true); };
  const abrirEdit = (f) => { setEditing(f); setForm({ ...f }); setOpen(true); };

  const salvar = async () => {
    if (!form.nome.trim()) { toast({ title: "Informe o nome/razão social" }); return; }
    try {
      await salvarFornecedor(editing ? { ...form, id: editing.id } : form);
      toast({ title: editing ? "Fornecedor atualizado" : "Fornecedor cadastrado" });
      setOpen(false);
    } catch (e) { toast({ title: "Erro ao salvar", description: e?.response?.data?.detail || "" }); }
  };
  const remover = async (f) => { await excluirFornecedor(f.id); toast({ title: "Fornecedor excluído" }); };

  const lista = (fornecedores || []).filter((f) =>
    f.nome.toLowerCase().includes(q.toLowerCase()) || (f.categoria || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="fornecedores-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Fornecedores</h1>
          <p className="text-slate-400 text-sm mt-0.5">Cadastro de fornecedores e prestadores de serviço</p>
        </div>
        <Button onClick={abrirNovo} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="novo-fornecedor-btn"><Plus className="w-4 h-4" /> Novo Fornecedor</Button>
      </div>

      <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 max-w-sm">
        <Search className="w-4 h-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou categoria..." className="flex-1 bg-transparent outline-none text-sm text-slate-700" data-testid="fornecedor-busca" />
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3 font-medium">Nome / Razão social</th>
                <th className="px-5 py-3 font-medium">Documento</th>
                <th className="px-5 py-3 font-medium">Categoria</th>
                <th className="px-5 py-3 font-medium">Contato</th>
                <th className="px-5 py-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {lista.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">Nenhum fornecedor cadastrado.</td></tr>
              )}
              {lista.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50/60" data-testid={`fornecedor-row-${f.id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-700 flex items-center gap-2"><Truck className="w-4 h-4 text-slate-400" /> {f.nome}</td>
                  <td className="px-5 py-3.5 text-slate-500">{f.documento || "-"}</td>
                  <td className="px-5 py-3.5">{f.categoria ? <span className="text-xs font-semibold px-2 py-1 rounded-md text-blue-700 bg-blue-50">{f.categoria}</span> : "-"}</td>
                  <td className="px-5 py-3.5 text-slate-500 text-xs">
                    {f.telefone && <span className="inline-flex items-center gap-1 mr-2"><Phone className="w-3 h-3" /> {f.telefone}</span>}
                    {f.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {f.email}</span>}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button onClick={() => abrirEdit(f)} className="text-slate-400 hover:text-blue-600 p-1.5" data-testid={`editar-fornecedor-${f.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remover(f)} className="text-slate-400 hover:text-red-600 p-1.5" data-testid={`excluir-fornecedor-${f.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-lg" data-testid="fornecedor-modal">
          <DialogHeader><DialogTitle>{editing ? "Editar Fornecedor" : "Novo Fornecedor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Nome / Razão social</label>
              <input className={inputCls} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="fornecedor-nome" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">CNPJ / CPF</label>
                <input className={inputCls} value={form.documento} onChange={(e) => setForm({ ...form, documento: e.target.value })} placeholder="CNPJ ou CPF" data-testid="fornecedor-documento" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Categoria</label>
                <input className={inputCls} value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} placeholder="ex.: Alimentos, Medicamentos, Serviços" data-testid="fornecedor-categoria" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Telefone</label>
                <input className={inputCls} value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="fornecedor-telefone" /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">E-mail</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Endereço</label>
              <input className={inputCls} value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} /></div>
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Observações</label>
              <textarea rows={2} className={inputCls} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={salvar} data-testid="salvar-fornecedor">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Fornecedores;
